import {
  CloudDownloadError,
  type CloudDownloadFailure,
} from './CloudDownloadError'
import {
  HTTP_FIRST_SERVER_ERROR,
  HTTP_FORBIDDEN,
  HTTP_NOT_FOUND,
  HTTP_TOO_MANY_REQUESTS,
} from './httpStatus'

export interface DownloadTiming {
  stallTimeoutMilliseconds: number
  retryDelayMilliseconds: number
}

// A stall, not a total, so a slow line can still bring in a 100 MB file.
const DEFAULT_TIMING: DownloadTiming = {
  stallTimeoutMilliseconds: 30_000,
  retryDelayMilliseconds: 2_000,
}

const RETRYABLE_FAILURES: CloudDownloadFailure[] = [
  'rate-limited',
  'server-error',
]

const failureForStatus = (status: number): CloudDownloadFailure => {
  if (status === HTTP_NOT_FOUND) return 'not-found'
  if (status === HTTP_FORBIDDEN) return 'forbidden'
  if (status === HTTP_TOO_MANY_REQUESTS) return 'rate-limited'
  if (status >= HTTP_FIRST_SERVER_ERROR) return 'server-error'
  return 'unexpected-status'
}

const startStallWatchdog = (timeoutMilliseconds: number) => {
  const controller = new AbortController()
  let timer = setTimeout(() => controller.abort(), timeoutMilliseconds)
  return {
    signal: controller.signal,
    hasFired: () => controller.signal.aborted,
    feed: () => {
      clearTimeout(timer)
      timer = setTimeout(() => controller.abort(), timeoutMilliseconds)
    },
    stop: () => clearTimeout(timer),
  }
}

type StallWatchdog = ReturnType<typeof startStallWatchdog>

const networkFailure = (
  watchdog: StallWatchdog,
  failure: CloudDownloadFailure,
) => new CloudDownloadError(watchdog.hasFired() ? 'timed-out' : failure)

// Each catch drops the original error on purpose: a fetch error can carry the link.
const fetchResponse = async (
  link: string,
  headers: HeadersInit,
  watchdog: StallWatchdog,
) => {
  try {
    return await fetch(link, { headers, signal: watchdog.signal })
  } catch {
    throw networkFailure(watchdog, 'cannot-connect')
  }
}

const readChunks = async (
  body: ReadableStream<Uint8Array<ArrayBuffer>>,
  watchdog: StallWatchdog,
) => {
  const reader = body.getReader()
  const chunks: Uint8Array<ArrayBuffer>[] = []
  for (let read = await reader.read(); !read.done; read = await reader.read()) {
    chunks.push(read.value)
    watchdog.feed()
  }
  return chunks
}

const readBody = async (response: Response, watchdog: StallWatchdog) => {
  if (!response.body) return new Blob([])
  try {
    return new Blob(await readChunks(response.body, watchdog))
  } catch {
    throw networkFailure(watchdog, 'dropped')
  }
}

const attemptDownload = async (
  link: string,
  timing: DownloadTiming,
  headers: HeadersInit,
) => {
  const watchdog = startStallWatchdog(timing.stallTimeoutMilliseconds)
  try {
    const response = await fetchResponse(link, headers, watchdog)
    if (!response.ok) {
      await response.body?.cancel()
      throw new CloudDownloadError(failureForStatus(response.status))
    }
    return await readBody(response, watchdog)
  } finally {
    watchdog.stop()
  }
}

const isRetryable = (error: unknown) =>
  error instanceof CloudDownloadError &&
  RETRYABLE_FAILURES.includes(error.failure)

export const bearerAuthorization = (token: string) => ({
  Authorization: `Bearer ${token}`,
})

const wait = (milliseconds: number) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds))

// A rate limit or a server error gets one more try. Anything else fails at once.
// A provider token goes in headers, never in the link: links reach browser history and logs.
export const downloadFromLink = async (
  link: string,
  timing: DownloadTiming = DEFAULT_TIMING,
  headers: HeadersInit = {},
): Promise<Blob> => {
  try {
    return await attemptDownload(link, timing, headers)
  } catch (error) {
    if (!isRetryable(error)) throw error
    await wait(timing.retryDelayMilliseconds)
    return attemptDownload(link, timing, headers)
  }
}
